'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { IReviewTestimonial } from '@/sanity/lib/definitions';
import { resolveI18nRichtext } from '@/sanity/globals/I18n';
import Image from 'next/image';
import bgDotsWhite from '@/public/reviews/bg-dots-white.png';

export interface IReviewsGridSectionData {
  reviews?: IReviewTestimonial[];
}

const ease = [0.25, 0.1, 0.25, 1] as const;

function QuoteIcon() {
  return (
    <svg
      className="shrink-0 rotate-180"
      width="32"
      height="24"
      viewBox="0 0 32 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.3107 0V9.85467C13.3107 17.46 8.336 22.6147 1.33333 24L0.00666667 21.132C3.24933 19.9093 5.33333 16.2813 5.33333 13.3333H0V0H13.3107ZM32 0V9.85467C32 17.46 27.0027 22.616 20 24L18.672 21.132C21.916 19.9093 24 16.2813 24 13.3333H18.6893V0H32Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ReviewCard({
  review,
  index,
  language,
}: {
  review: IReviewTestimonial;
  index: number;
  language?: string;
}) {
  const quote = resolveI18nRichtext(review.quote, review.i18n, language);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease, delay: (index % 3) * 0.08 }}
      className="group relative mb-[3rem] break-inside-avoid overflow-hidden rounded-[0.25rem] border-[.125rem] border-black/10 bg-white px-[5.75rem] py-[4.25rem]"
    >
      {/* Dots background */}
      <div className="pointer-events-none absolute inset-0 mix-blend-exclusion opacity-70">
        <Image alt="" fill className="object-cover" src={bgDotsWhite} />
      </div>

      {/* Left accent bar — hidden by default, appears on hover */}
      <div className="absolute inset-y-0 left-0 z-10 w-[0.375rem] bg-[#228E71] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      {/* Content */}
      <div className="relative z-10">
        {/* Quote icon + date */}
        <div className="mb-[4rem] flex items-end gap-[1.75rem]">
          <div className="text-[#003828] transition-colors duration-300 group-hover:text-[#228E71]">
            <QuoteIcon />
          </div>
          {review.date && (
            <p className="mb-0 font-geist-mono text-[1.75rem] font-semibold uppercase leading-[1.33] text-[#003828] opacity-[0.43]">
              {review.date}
            </p>
          )}
        </div>

        {/* Quote text */}
        {quote && (
          <div className="richtext richtext-start mb-[4.5rem] prose-p:mb-0 prose-p:text-[2.75rem] prose-p:font-normal prose-p:leading-[1.3] prose-p:tracking-[-0.0825rem] [&_p]:text-black">
            <PortableText value={quote} components={richTextComponent} />
          </div>
        )}

        {/* Author row */}
        <div className="flex items-center gap-[2rem]">
          <div className="h-[3rem] w-px bg-black/30" />
          <div className="flex flex-wrap items-center gap-x-[1.875rem] gap-y-[0.5rem]">
            {review.authorName && (
              <p className="mb-0 font-geist-mono text-[2rem] font-semibold uppercase leading-[1.33] text-black">
                {review.authorName}
              </p>
            )}
            {review.authorRole && (
              <>
                <div className="h-[1.125rem] w-px bg-black/40" />
                <p className="mb-0 font-geist-mono text-[1.75rem] font-semibold uppercase leading-[1.33] text-black opacity-[0.43]">
                  {review.authorRole}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function ReviewsGridSection({
  data,
  language,
}: {
  data: IReviewsGridSectionData;
  language?: string;
}) {
  if (!data.reviews?.length) return null;

  return (
    <section className="w-full pb-[10rem] pt-0 lg:pb-[15rem]">
      <div className="mx-auto w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))]">
        <div className="columns-1 gap-[3rem] md:columns-2 lg:columns-3">
          {data.reviews.map((review: IReviewTestimonial, index: number) => (
            <ReviewCard
              key={review._id ?? index}
              review={review}
              index={index}
              language={language}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
